/**
 * `<caos-ordered-picker>` — choose several values from a list, and put them in order.
 * (Catalog: Fields & input › OrderedPicker — choose several values from a list and put them in order, in two lists side by side.)
 *
 * CAOS-597. Choosing entries and ordering them — which shell entries are pinned and in what order,
 * which columns a list shows and in what order — was assembled by hand wherever an admin screen
 * needed it: a row per chosen entry with its own up and down buttons, and a separate row of "+"
 * buttons for everything else. This is the one piece for it.
 *
 * TWO LISTS. `options` is everything that could be chosen, as `{ value, label, qualifier? }` (or plain
 * strings). `value` is what is chosen, in order. The available list holds the options not chosen, in
 * the order `options` gives them; the chosen list holds `value` in its own order, numbered.
 *
 * WHAT CANNOT GO WRONG BY ACCIDENT.
 *   • A `locked` value is chosen and stays chosen: it is added to `value` when missing, drawn with a
 *     lock, and Remove refuses it.
 *   • `max` refuses an add past it. `min` does not refuse a removal — swapping one entry for another
 *     passes through one too few — but says so under the lists until it holds again.
 *   • A chosen value that is not among the options stays in the chosen list, marked as not among the
 *     options, so it can be removed deliberately rather than dropped without anybody noticing.
 *
 * KEYBOARD. Each list is one tab stop. Arrow Up and Down, Home and End move the highlight through it;
 * Enter or Space moves the highlighted entry to the other list; Alt with Arrow Up or Down moves a
 * chosen entry up or down. The buttons between and beside the lists do the same by pointer, and a
 * double-click moves an entry across.
 *
 * A bubbling, composed `change` fires after every move, with `value` already updated. Setting
 * `options`, `value` or `locked` fires nothing. Styling seams: `::part(picker)`, `::part(label)`,
 * `::part(heading)`, `::part(listbox)`, `::part(option)`, `::part(rank)`, `::part(button)`,
 * `::part(message)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export interface CaosOrderedPickerOption {
  value: string;
  label: string;
  /** A quieter second line under the label — what the option is, when the label alone will not say. */
  qualifier?: string;
}

type Side = 'available' | 'chosen';

interface Entry {
  readonly value: string;
  readonly label: string;
  readonly qualifier: string;
  readonly missing: boolean;
  readonly locked: boolean;
}

const NOT_AMONG_OPTIONS = 'Not among the options';

let uid = 0;

/** Strings only, first occurrence kept. */
function strings(list: unknown): string[] {
  const seen = new Set<string>();
  return (Array.isArray(list) ? list : []).filter((v): v is string => {
    if (typeof v !== 'string' || seen.has(v)) return false;
    seen.add(v);
    return true;
  });
}

const sheet = makeSheet(`
  :host { display: block; }

  .picker { display: flex; flex-direction: column; gap: var(--caos-space-2); }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }

  .columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--caos-space-2);
  }
  .column { display: flex; flex-direction: column; gap: var(--caos-space-1); min-width: 0; align-self: stretch; }
  .heading { font-family: var(--caos-font); font-size: 12px; color: var(--caos-color-text-muted); }

  .list {
    flex: 1 1 auto;
    margin: 0;
    padding: var(--caos-space-1);
    list-style: none;
    min-height: 168px;
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface);
  }
  .list:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .option {
    display: flex;
    align-items: flex-start;
    gap: var(--caos-space-2);
    padding: var(--caos-space-2);
    border-radius: var(--caos-radius-sm);
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
    line-height: 1.35;
    color: var(--caos-color-text);
    cursor: pointer;
    user-select: none;
  }
  .option:hover { background: var(--caos-color-surface-2); }
  .option[aria-selected="true"] { background: var(--caos-state-selected-bg); }

  .rank {
    flex: none;
    min-width: 1.5em;
    text-align: end;
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text-muted);
  }
  .text { display: flex; flex: 1 1 auto; flex-direction: column; gap: 1px; min-width: 0; overflow-wrap: anywhere; }
  .sub { font-size: 11.5px; line-height: 1.3; color: var(--caos-color-text-muted); }
  .is-missing .sub { color: var(--caos-color-danger); }
  .lock { flex: none; width: 14px; height: 14px; margin-top: 2px; color: var(--caos-color-text-muted); }
  .lock svg { display: block; width: 100%; height: 100%; }
  .unseen {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
  .empty { padding: var(--caos-space-2); font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }

  .buttons { display: flex; flex-direction: column; gap: var(--caos-space-1); }
  .button {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0;
    padding: 0;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    cursor: pointer;
  }
  .button:hover:not(:disabled) { border-color: var(--caos-color-border-strong); }
  .button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .button:disabled { cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); }
  .button svg { display: block; width: 16px; height: 16px; }
  /* The chevron points down; turned, it points toward the list an entry moves to. */
  .to-chosen svg { transform: rotate(-90deg); }
  .to-available svg { transform: rotate(90deg); }
  :host(:dir(rtl)) .to-chosen svg { transform: rotate(90deg); }
  :host(:dir(rtl)) .to-available svg { transform: rotate(-90deg); }
  .up svg { transform: rotate(180deg); }

  .message { font-family: var(--caos-font); font-size: 11.5px; line-height: 1.4; color: var(--caos-color-text-muted); }
  .message:empty { display: none; }
  .message.is-refusal { color: var(--caos-color-danger); }

  :host([disabled]) .list { opacity: var(--caos-state-disabled-opacity); }
  :host([disabled]) .option { cursor: not-allowed; }
`);

export class CaosOrderedPicker extends CaosElement {
  static get observedAttributes(): string[] {
    return ['label', 'available-label', 'chosen-label', 'min', 'max', 'disabled', 'message'];
  }

  private _options: CaosOrderedPickerOption[] = [];
  private _value: string[] = [];
  private _locked: string[] = [];
  private readonly highlighted: Record<Side, string | null> = { available: null, chosen: null };
  private readonly idBase = `caos-ordered-picker-${++uid}`;
  private readonly labelEl: HTMLSpanElement;
  private readonly messageEl: HTMLDivElement;
  private readonly headings: Record<Side, HTMLSpanElement>;
  private readonly lists: Record<Side, HTMLUListElement>;
  private readonly toChosen: HTMLButtonElement;
  private readonly toAvailable: HTMLButtonElement;
  private readonly upButton: HTMLButtonElement;
  private readonly downButton: HTMLButtonElement;

  constructor() {
    super(sheet);
    const picker = document.createElement('div');
    picker.className = 'picker';
    picker.setAttribute('part', 'picker');

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.id = `${this.idBase}-label`;
    this.labelEl.setAttribute('part', 'label');

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.id = `${this.idBase}-message`;
    this.messageEl.setAttribute('part', 'message');
    this.messageEl.setAttribute('aria-live', 'polite');

    this.headings = { available: this.makeHeading('available'), chosen: this.makeHeading('chosen') };
    this.lists = { available: this.makeList('available'), chosen: this.makeList('chosen') };

    this.toChosen = this.makeButton('to-chosen', 'Add to chosen', () => this.moveAcross('available'));
    this.toAvailable = this.makeButton('to-available', 'Remove from chosen', () => this.moveAcross('chosen'));
    this.upButton = this.makeButton('up', 'Move up', () => this.reorder(-1));
    this.downButton = this.makeButton('down', 'Move down', () => this.reorder(1));

    const across = document.createElement('div');
    across.className = 'buttons';
    across.append(this.toChosen, this.toAvailable);
    const order = document.createElement('div');
    order.className = 'buttons';
    order.append(this.upButton, this.downButton);

    const columns = document.createElement('div');
    columns.className = 'columns';
    columns.append(this.makeColumn('available'), across, this.makeColumn('chosen'), order);

    picker.append(this.labelEl, columns, this.messageEl);
    this.root.append(picker);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** Everything that could be chosen. */
  get options(): CaosOrderedPickerOption[] {
    return this._options.map((o) => ({ ...o }));
  }
  set options(list: ReadonlyArray<CaosOrderedPickerOption | string>) {
    const seen = new Set<string>();
    this._options = (Array.isArray(list) ? list : []).flatMap((item: CaosOrderedPickerOption | string) => {
      const o = typeof item === 'string' ? { value: item, label: item } : item;
      if (!o || typeof o.value !== 'string' || seen.has(o.value)) return [];
      seen.add(o.value);
      const label = typeof o.label === 'string' && o.label ? o.label : o.value;
      return [typeof o.qualifier === 'string' && o.qualifier ? { value: o.value, label, qualifier: o.qualifier } : { value: o.value, label }];
    });
    this.render();
  }

  /** What is chosen, in order. */
  get value(): string[] {
    return [...this._value];
  }
  set value(list: readonly string[]) {
    this._value = this.withLocked(strings(list));
    this.render();
  }

  /** Values that are chosen and cannot be removed. */
  get locked(): string[] {
    return [...this._locked];
  }
  set locked(list: readonly string[]) {
    this._locked = strings(list);
    this._value = this.withLocked(this._value);
    this.render();
  }

  get min(): number {
    return this.count('min');
  }
  set min(v: number) {
    this.setCount('min', v);
  }

  get max(): number {
    return this.count('max');
  }
  set max(v: number) {
    this.setCount('max', v);
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(v: boolean) {
    this.toggleAttribute('disabled', v);
  }

  private count(name: string): number {
    const n = Number(this.getAttribute(name));
    return Number.isInteger(n) && n > 0 ? n : 0;
  }

  private setCount(name: string, v: number): void {
    if (Number.isInteger(v) && v > 0) this.setAttribute(name, String(v));
    else this.removeAttribute(name);
  }

  private withLocked(value: string[]): string[] {
    return [...value, ...this._locked.filter((v) => !value.includes(v))];
  }

  private makeHeading(side: Side): HTMLSpanElement {
    const heading = document.createElement('span');
    heading.className = 'heading';
    heading.id = `${this.idBase}-${side}-heading`;
    heading.setAttribute('part', 'heading');
    return heading;
  }

  private makeList(side: Side): HTMLUListElement {
    const list = document.createElement('ul');
    list.className = 'list';
    list.dataset.side = side;
    list.setAttribute('part', 'listbox');
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', `${this.labelEl.id} ${this.idBase}-${side}-heading`);
    list.setAttribute('aria-describedby', this.messageEl.id);
    list.tabIndex = 0;
    list.addEventListener('keydown', (e) => this.onListKeydown(side, e));
    list.addEventListener('focus', () => {
      if (this.highlighted[side] === null && !this.disabled) {
        const first = this.entries(side)[0];
        if (first) this.highlight(side, first.value);
      }
    });
    return list;
  }

  private makeColumn(side: Side): HTMLDivElement {
    const column = document.createElement('div');
    column.className = 'column';
    column.append(this.headings[side], this.lists[side]);
    return column;
  }

  private makeButton(kind: string, label: string, onPress: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `button ${kind}`;
    button.setAttribute('part', 'button');
    button.setAttribute('aria-label', label);
    button.title = label;
    button.append(makeIcon('chevron'));
    button.addEventListener('click', onPress);
    return button;
  }

  private entries(side: Side): Entry[] {
    if (side === 'available') {
      const chosen = new Set(this._value);
      return this._options
        .filter((o) => !chosen.has(o.value))
        .map((o) => ({ value: o.value, label: o.label, qualifier: o.qualifier ?? '', missing: false, locked: false }));
    }
    const byValue = new Map(this._options.map((o) => [o.value, o]));
    const locked = new Set(this._locked);
    return this._value.map((value) => {
      const o = byValue.get(value);
      return {
        value,
        label: o ? o.label : value,
        qualifier: o ? (o.qualifier ?? '') : NOT_AMONG_OPTIONS,
        missing: !o,
        locked: locked.has(value),
      };
    });
  }

  private render(): void {
    this.labelEl.textContent = this.getAttribute('label') ?? '';
    this.headings.available.textContent = this.getAttribute('available-label') || 'Available';
    this.headings.chosen.textContent = this.getAttribute('chosen-label') || 'Chosen';
    this.renderList('available');
    this.renderList('chosen');
    this.syncControls();
  }

  private renderList(side: Side): void {
    const list = this.lists[side];
    const entries = this.entries(side);
    if (!entries.some((e) => e.value === this.highlighted[side])) this.highlighted[side] = null;

    list.replaceChildren();
    if (entries.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = side === 'available' ? 'Everything is chosen.' : 'Nothing chosen yet.';
      list.append(empty);
    }
    entries.forEach((entry, i) => {
      const li = document.createElement('li');
      li.className = 'option';
      li.classList.toggle('is-missing', entry.missing);
      li.classList.toggle('is-locked', entry.locked);
      li.id = `${this.idBase}-${side}-${i}`;
      li.dataset.value = entry.value;
      li.setAttribute('role', 'option');
      li.setAttribute('part', 'option');
      li.setAttribute('aria-selected', String(entry.value === this.highlighted[side]));

      if (side === 'chosen') {
        const rank = document.createElement('span');
        rank.className = 'rank';
        rank.setAttribute('part', 'rank');
        rank.setAttribute('aria-hidden', 'true');
        rank.textContent = String(i + 1);
        li.append(rank);
      }

      const text = document.createElement('span');
      text.className = 'text';
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = entry.label;
      text.append(name);
      if (entry.qualifier) {
        const sub = document.createElement('span');
        sub.className = 'sub';
        sub.textContent = entry.qualifier;
        text.append(sub);
      }
      li.append(text);

      if (entry.locked) {
        const lock = document.createElement('span');
        lock.className = 'lock';
        lock.setAttribute('aria-hidden', 'true');
        lock.append(makeIcon('lock'));
        const words = document.createElement('span');
        words.className = 'unseen';
        words.textContent = 'Required, cannot be removed';
        li.append(lock, words);
      }

      li.addEventListener('click', () => {
        if (this.disabled) return;
        this.highlight(side, entry.value);
        list.focus();
      });
      li.addEventListener('dblclick', () => {
        if (this.disabled) return;
        this.highlight(side, entry.value);
        this.moveAcross(side);
      });
      list.append(li);
    });

    const active = entries.findIndex((e) => e.value === this.highlighted[side]);
    if (active >= 0) list.setAttribute('aria-activedescendant', `${this.idBase}-${side}-${active}`);
    else list.removeAttribute('aria-activedescendant');
  }

  private syncControls(): void {
    const disabled = this.disabled;
    const { min, max } = this;
    const count = this._value.length;
    const atMax = max > 0 && count >= max;
    const belowMin = min > 0 && count < min;
    const chosen = this.highlighted.chosen;
    const index = chosen === null ? -1 : this._value.indexOf(chosen);

    this.toChosen.disabled = disabled || this.highlighted.available === null || atMax;
    this.toAvailable.disabled = disabled || chosen === null || this._locked.includes(chosen);
    this.upButton.disabled = disabled || index <= 0;
    this.downButton.disabled = disabled || index < 0 || index >= count - 1;

    for (const list of [this.lists.available, this.lists.chosen]) {
      list.tabIndex = disabled ? -1 : 0;
      if (disabled) list.setAttribute('aria-disabled', 'true');
      else list.removeAttribute('aria-disabled');
    }
    if (belowMin) this.lists.chosen.setAttribute('aria-invalid', 'true');
    else this.lists.chosen.removeAttribute('aria-invalid');

    this.messageEl.textContent = belowMin
      ? `Choose at least ${min}.`
      : atMax
        ? `No more than ${max} can be chosen.`
        : (this.getAttribute('message') ?? '');
    this.messageEl.classList.toggle('is-refusal', belowMin);
  }

  private highlight(side: Side, value: string): void {
    this.highlighted[side] = value;
    this.renderList(side);
    this.syncControls();
    this.lists[side].querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Move the highlighted entry of one list to the other. */
  private moveAcross(from: Side): void {
    const value = this.highlighted[from];
    if (this.disabled || value === null) return;
    if (from === 'available') {
      if (this.max > 0 && this._value.length >= this.max) return;
      const entries = this.entries('available');
      const i = entries.findIndex((e) => e.value === value);
      this._value = [...this._value, value];
      this.highlighted.available = entries[i + 1]?.value ?? entries[i - 1]?.value ?? null;
      this.highlighted.chosen = value;
    } else {
      if (this._locked.includes(value)) return;
      const i = this._value.indexOf(value);
      this._value = this._value.filter((v) => v !== value);
      this.highlighted.chosen = this._value[i] ?? this._value[i - 1] ?? null;
      if (this._options.some((o) => o.value === value)) this.highlighted.available = value;
    }
    this.render();
    this.emitChange();
  }

  /** Move the highlighted chosen entry one place up (-1) or down (1). */
  private reorder(delta: -1 | 1): void {
    const value = this.highlighted.chosen;
    if (this.disabled || value === null) return;
    const i = this._value.indexOf(value);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= this._value.length) return;
    const next = [...this._value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    this._value = next;
    this.render();
    this.emitChange();
  }

  private onListKeydown(side: Side, e: KeyboardEvent): void {
    if (this.disabled) return;
    const entries = this.entries(side);
    if (entries.length === 0) return;
    const current = entries.findIndex((x) => x.value === this.highlighted[side]);
    const go = (i: number): void => {
      e.preventDefault();
      this.highlight(side, entries[Math.max(0, Math.min(entries.length - 1, i))]!.value);
    };
    switch (e.key) {
      case 'ArrowDown':
        if (e.altKey && side === 'chosen') {
          e.preventDefault();
          this.reorder(1);
        } else go(current < 0 ? 0 : current + 1);
        break;
      case 'ArrowUp':
        if (e.altKey && side === 'chosen') {
          e.preventDefault();
          this.reorder(-1);
        } else go(current < 0 ? 0 : current - 1);
        break;
      case 'Home':
        go(0);
        break;
      case 'End':
        go(entries.length - 1);
        break;
      case 'Enter':
      case ' ':
        if (current >= 0) {
          e.preventDefault();
          this.moveAcross(side);
        }
        break;
    }
  }

  private emitChange(): void {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }
}

define('caos-ordered-picker', CaosOrderedPicker);

declare global {
  interface HTMLElementTagNameMap {
    'caos-ordered-picker': CaosOrderedPicker;
  }
}
